import { CollectDataFormItemType, CollectDataStepType } from 'src/constants/collect-data/enums'
import type {
  BackgroundCheckStepConfig,
  FormStepConfig,
  LmsTrainingStepConfig,
  MvrUploadStepConfig,
  SignContractStepConfig,
  TextBlockFormItem,
  OnboardingTemplateStep,
} from 'src/interfaces/onboardingTemplate'

function typedStepValidationLabel(step: OnboardingTemplateStep, kind: string): string {
  const stepLabelText = step.collectDataTemplateStepLabel.trim()

  if (stepLabelText !== '') {
    return `${kind} ("${stepLabelText}")`
  }

  return kind
}

function bgcMvrStepValidationLabel(step: OnboardingTemplateStep): string {
  const kind = step.type === CollectDataStepType.BGC ? 'Background Check' : 'MVR Upload'

  return typedStepValidationLabel(step, kind)
}

export function getFlowEditorYardstikPackageSaveMessage(
  editorSteps: OnboardingTemplateStep[],
): string | null {
  for (const step of editorSteps) {
    if (step.type !== CollectDataStepType.BGC && step.type !== CollectDataStepType.MVR) {
      continue
    }

    const stepConfig = step.config as BackgroundCheckStepConfig | MvrUploadStepConfig
    const label = bgcMvrStepValidationLabel(step)

    if (!stepConfig.enableYardstik && !stepConfig.manualUpload) {
      return `${label}: enable Yardstik, Manual Upload, or both before saving.`
    }

    if (stepConfig.enableYardstik) {
      const pkgTrimmed =
        typeof stepConfig.yardstikPackage === 'string' ? stepConfig.yardstikPackage.trim() : ''

      if (pkgTrimmed === '') {
        return `${label}: select a Yardstik package when Enable Yardstik is on.`
      }
    }
  }

  return null
}

export function getFlowEditorLmsTrainingSaveMessage(
  editorSteps: OnboardingTemplateStep[],
): string | null {
  for (const step of editorSteps) {
    if (step.type !== CollectDataStepType.LMSTraining) {
      continue
    }

    const stepConfig = step.config as LmsTrainingStepConfig
    const label = typedStepValidationLabel(step, 'LMS Training')
    const trainingId =
      typeof stepConfig.lmsTrainingId === 'string' ? stepConfig.lmsTrainingId.trim() : ''

    if (trainingId === '') {
      return `${label}: select an LMS training before saving.`
    }
  }

  return null
}

export function getFlowEditorSignContractSaveMessage(
  editorSteps: OnboardingTemplateStep[],
): string | null {
  for (const step of editorSteps) {
    if (step.type !== CollectDataStepType.SignContract) {
      continue
    }

    const stepConfig = step.config as SignContractStepConfig
    const label = typedStepValidationLabel(step, 'Sign Contract')
    const templateId =
      typeof stepConfig.docusignTemplateId === 'string' ? stepConfig.docusignTemplateId.trim() : ''

    if (templateId === '') {
      return `${label}: select a DocuSign template before saving.`
    }
  }

  return null
}

function formStepLabelForValidation(
  step: OnboardingTemplateStep,
  formConfig: FormStepConfig,
): string {
  const formLabel = formConfig.label.trim()

  if (formLabel !== '') {
    return formLabel
  }

  const stepLabelText = step.collectDataTemplateStepLabel.trim()

  if (stepLabelText !== '') {
    return stepLabelText
  }

  return 'Form'
}

/**
 * When non-null, save must be blocked: a Form step fails collect-data template validation
 * (non-empty formItems, text block `text`, titled fields, etc.).
 */
export function getFlowEditorFormStepSaveMessage(
  editorSteps: OnboardingTemplateStep[],
): string | null {
  for (const step of editorSteps) {
    if (step.type !== CollectDataStepType.Form) {
      continue
    }

    const formConfig = step.config as FormStepConfig
    const name = formStepLabelForValidation(step, formConfig)

    if (formConfig.name.trim() === '') {
      return `${name}: enter a form name before saving.`
    }

    if (formConfig.description.trim() === '') {
      return `${name}: enter a form description before saving.`
    }

    const items = formConfig.formItems ?? []

    if (items.length === 0) {
      return `${name}: add at least one form item. The server rejects an empty form builder.`
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const ord = i + 1

      if (item === null || item === undefined) {
        continue
      }

      switch (item.type) {
        case CollectDataFormItemType.TextBlock: {
          const tb = item as TextBlockFormItem & { text?: string }
          let body = ''

          if (typeof tb.text === 'string' && tb.text.trim() !== '') {
            body = tb.text.trim()
          } else if (typeof tb.content === 'string') {
            body = tb.content.trim()
          }

          if (body === '') {
            return `${name}: text block #${ord} needs body text before saving.`
          }

          break
        }

        case CollectDataFormItemType.Video: {
          if (item.title.trim() === '' || item.url.trim() === '') {
            return `${name}: video item #${ord} needs a title and URL.`
          }

          break
        }

        case CollectDataFormItemType.TextInput:
        case CollectDataFormItemType.Date:
        case CollectDataFormItemType.Number:
        case CollectDataFormItemType.File: {
          if (item.title.trim() === '') {
            return `${name}: item #${ord} (${item.type}) needs a title.`
          }

          break
        }

        case CollectDataFormItemType.Select:
        case CollectDataFormItemType.Radio:
        case CollectDataFormItemType.Checkbox: {
          if (item.title.trim() === '') {
            return `${name}: item #${ord} (${item.type}) needs a title.`
          }

          if (!Array.isArray(item.options) || item.options.length === 0) {
            return `${name}: item #${ord} (${item.type}) needs at least one option.`
          }

          if (item.checkAnswers && !item.options.some((o) => o.isCorrect)) {
            const itemTitle = item.title.trim() !== '' ? `"${item.title.trim()}"` : `#${ord}`

            return `${name}: item ${itemTitle} has Check answers enabled — mark at least one valid answer.`
          }

          break
        }

        default:
          break
      }
    }
  }

  return null
}
